/**
 * NotificationTemplatesPage — `/settings/notification-templates`.
 *
 * Manage SMS / email / push notification templates via
 * `GET /v1/notifications/templates` on service-notification (B slice 6).
 *
 * Supports a `?channel=EMAIL|SMS|PUSH|ALL` URL query so deep-links (e.g. the
 * legacy `/admin/email-templates` redirect) can pre-select a channel filter.
 */
import { type JSX, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Bell, Mail, MessageSquare, Smartphone, X } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  listNotificationTemplates,
  updateNotificationTemplate,
  type NotificationTemplate,
} from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

type ChannelFilter = 'ALL' | NotificationTemplate['channel'];

function parseChannelParam(raw: string | null): ChannelFilter {
  const upper = (raw ?? '').toUpperCase();
  if (upper === 'EMAIL' || upper === 'SMS' || upper === 'PUSH' || upper === 'ALL') {
    return upper;
  }
  return 'ALL';
}

const CHANNEL_ICONS: Record<NotificationTemplate['channel'], typeof Mail> = {
  EMAIL: Mail,
  SMS: MessageSquare,
  PUSH: Smartphone,
};

interface EditState {
  template: NotificationTemplate;
  body: string;
  subject: string;
}

export function NotificationTemplatesPage(): JSX.Element {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<ChannelFilter>(() =>
    parseChannelParam(searchParams.get('channel')),
  );
  const [edit, setEdit] = useState<EditState | null>(null);
  const [saveError, setSaveError] = useState('');

  // Keep state ↔ URL in sync. Reading the param first lets a deep-link land
  // directly in the right tab; writing it back lets a user share the filter.
  useEffect(() => {
    const next = parseChannelParam(searchParams.get('channel'));
    if (next !== filter) setFilter(next);
  }, [searchParams, filter]);

  function selectChannel(next: ChannelFilter): void {
    setFilter(next);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'ALL') {
          params.delete('channel');
        } else {
          params.set('channel', next);
        }
        return params;
      },
      { replace: true },
    );
  }

  const templates = useQuery({
    queryKey: ['notifications', 'templates'],
    queryFn: () => listNotificationTemplates(),
    retry: false,
    staleTime: 60_000,
  });

  const saveMutation = useMutation({
    mutationFn: (args: { id: string; body: string; subject?: string | null }) =>
      updateNotificationTemplate(args.id, { body: args.body, subject: args.subject }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'templates'] });
      setEdit(null);
      setSaveError('');
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    },
  });

  const denied =
    templates.isError &&
    templates.error instanceof ApiError &&
    (templates.error.status === 401 || templates.error.status === 403);

  const list = templates.data?.items ?? [];
  const filtered = filter === 'ALL' ? list : list.filter((t) => t.channel === filter);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notification templates"
        description="Manage SMS, email and push notification templates."
        actions={<Bell className="w-4 h-4 text-muted" />}
      />

      {templates.isPending ? (
        <p className="text-sm text-muted">Loading templates…</p>
      ) : denied ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          <p className="font-semibold">Permission required</p>
          <p>The token does not carry <code className="font-mono">Settings/manageNotificationTemplates</code>.</p>
        </div>
      ) : templates.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          <p className="font-semibold">Template registry unreachable</p>
          <p>{(templates.error as Error).message}</p>
        </div>
      ) : null}

      {/* Edit drawer */}
      {edit ? (
        <div className="bg-white border border-border rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 text-sm">Edit: {edit.template.key}</h3>
            <button
              type="button"
              onClick={() => { setEdit(null); setSaveError(''); }}
              className="text-muted hover:text-gray-900"
              aria-label="Close editor"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {edit.template.channel === 'EMAIL' ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={edit.subject}
                onChange={(e) => setEdit((p) => p ? { ...p, subject: e.target.value } : p)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                aria-label="Template subject"
              />
            </div>
          ) : null}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Body</label>
            <textarea
              value={edit.body}
              onChange={(e) => setEdit((p) => p ? { ...p, body: e.target.value } : p)}
              rows={6}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
              aria-label="Template body"
            />
          </div>
          {saveError ? (
            <p className="text-xs text-red-600">{saveError}</p>
          ) : null}
          <div className="flex gap-3">
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={() =>
                saveMutation.mutate({
                  id: edit.template.id,
                  body: edit.body,
                  subject: edit.subject || null,
                })
              }
              className="px-4 py-2 text-sm font-medium text-white bg-nipost-green rounded-lg disabled:opacity-60"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save template'}
            </button>
            <button
              type="button"
              onClick={() => { setEdit(null); setSaveError(''); }}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-border rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit" role="tablist" aria-label="Channel filter">
        {(['ALL', 'SMS', 'EMAIL', 'PUSH'] as const).map((c) => (
          <button
            key={c}
            type="button"
            role="tab"
            aria-selected={filter === c}
            onClick={() => selectChannel(c)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium ${
              filter === c ? 'bg-white text-nipost-green shadow-sm' : 'text-muted hover:text-gray-900'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.length === 0 && !templates.isPending ? (
          <p className="col-span-3 text-sm text-muted text-center py-8">No templates found.</p>
        ) : (
          filtered.map((t) => {
            const Icon = CHANNEL_ICONS[t.channel];
            return (
              <div key={t.id} className="bg-white border border-border rounded-xl p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-nipost-green" />
                    <span className="text-[10px] font-mono uppercase text-muted">{t.channel}</span>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    {t.module}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                <p className="text-[10px] font-mono text-muted">{t.key}</p>
                {t.subject ? <p className="text-xs text-gray-700">{t.subject}</p> : null}
                <p className="text-xs text-gray-600 line-clamp-3">{t.body}</p>
                <button
                  type="button"
                  onClick={() => setEdit({ template: t, body: t.body, subject: t.subject ?? '' })}
                  className="mt-1 text-xs text-nipost-green font-medium hover:underline"
                >
                  Edit template
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
